import { api, toast, inspectorForm } from '../state.js';
import { esc, badge } from '/core/js/utils.js';
import { loadRules } from '/core/js/password-strength.js';
import { contributions } from '/core/js/modules.js';
import { card, failedCard, setBadge, runTest } from './settings-card.js';

/**
 * What this install is connected to, and whether each link works (chunk
 * 4.8d). Super Admin only. Read-only on purpose: every link is set in the
 * server's config files — getting one wrong breaks sign-in or hands over the
 * directory or the mail server (settings.md, decisions.md 2026-09-23).
 * Secrets show only as set / not set.
 *
 * The platform's cards are here: the sign-in directory (GET /ldap/config,
 * POST /ldap/test), the mail relay (GET/PUT /mail/config, POST /mail/test —
 * the one card that is editable here, Eric 2026-10-04; settings.md) and the
 * password rule. Each module's come between them,
 * as self-contained components it lists in its `settings` (core/js/modules.js)
 * — the accounts module's are <arc-accounts-connections>. Cards are built with
 * ./settings-card.js so they all read alike.
 */
const DOWN_POLICY = {
  reject: 'Sign-in is refused for directory accounts — an outage can never bring back a changed password.',
  mirror: 'The last password known here is accepted, even if it has since been changed in the directory.',
};

class ArcSettings extends HTMLElement {
  connectedCallback() {
    this.innerHTML = `
      <div class="page-header">
        <div class="detail-title"><h1>Settings</h1></div>
      </div>
      <p class="text-muted">What this install is connected to, and whether each link works. All but the mail relay are set in
        the configuration files on the server, not here; Test checks a link as it is set now.</p>
      <div id="st-body"><div class="loading-center"><div class="spinner"></div></div></div>`;

    // The platform's one Test button. A module's card handles its own
    // buttons; their clicks bubble up here too, and are left alone.
    this.querySelector('#st-body').addEventListener('click', e => {
      const btn = e.target.closest('button[data-test="signin"], button[data-test="relay"], button[data-edit="relay"]');
      if (!btn) return;
      if (btn.dataset.test === 'signin') runTest(this, btn, () => api.ldap.test());
      if (btn.dataset.test === 'relay')  runTest(this, btn, () => api.mail.test());
      if (btn.dataset.edit === 'relay')  this.#editRelay();
    });

    this.#load();
  }

  async #load() {
    const body = this.querySelector('#st-body');
    const [signIn, relay, rules] = await Promise.allSettled([api.ldap.config(), api.mail.config(), loadRules()]);

    if (signIn.status === 'rejected' && signIn.reason?.status === 403) {
      body.innerHTML = '<div class="empty-state"><h3>Super Admin only</h3><p>Only a Super Admin can see how this install is connected.</p></div>';
      return;
    }

    this.relay = relay.status === 'fulfilled' ? relay.value : null;
    body.innerHTML = [
      signIn.status === 'fulfilled' ? this.#signIn(signIn.value) : failedCard('Sign-in directory', signIn.reason),
      relay.status === 'fulfilled' ? this.#relay(relay.value) : failedCard('Mail relay', relay.reason),
      ...contributions('settings').map(tag => `<${tag}></${tag}>`),
      rules.status === 'fulfilled' ? this.#passwords(rules.value.policy) : '',
    ].join('');
  }

  #signIn(c) {
    const off = !c.enabled
      ? '<p class="alert alert-warning">Signing in with the directory is switched off: only local accounts can sign in.</p>' : '';
    return card('Sign-in directory', 'signin',
      'Where everyone signs in — to this console and to account settings — and who may.', [
        ['Server', `<span class="mono">${esc(c.uri || '—')}</span>`],
        ['Looks under', `<span class="mono">${esc(c.root || '—')}</span>`],
        ['Who may sign in', `<span class="mono">${esc(c.filter || 'everyone under it')}</span>`],
        ['Reads as', c.bindDN ? `<span class="mono">${esc(c.bindDN)}</span>` : 'anonymous'],
        ['Its password', setBadge(c.has_password)],
        ['If it cannot be reached', esc(DOWN_POLICY[c.down_policy] ?? c.down_policy)],
        ['Set in', `${esc(c.source)} (<code>ldap</code>)`],
      ], off);
  }

  // The relay every mail goes through: alerts, password resets, the Test. The one
  // card set here rather than in config.php; its password is kept sealed.
  #relay(r) {
    const src = (f) => r.sources?.[f] === 'console' ? '' : r.sources?.[f] === 'config' ? ' <span class="text-muted">(config.php)</span>' : '';
    const SEC = { tls: 'STARTTLS', ssl: 'SMTPS (TLS from the start)', '': 'none (plain)' };
    const warn = r.password_unreadable
      ? '<p class="alert alert-warning">The saved password can no longer be read (the session-signing key changed since it was saved). Set it again with Edit.</p>'
      : !r.configured ? '<p class="alert alert-warning">No relay is set: alerts and password-reset mail cannot go out. Edit to set one.</p>'
      : !r.enabled ? '<p class="alert alert-warning">The relay is switched off: nothing is sent.</p>' : '';
    return card('Mail relay', 'relay',
      'The SMTP server every mail from ARC goes through — backup alerts, password resets, this card\'s Test (which sends one message to you).', [
        ['Server', r.configured ? `<span class="mono">${esc(r.host)}:${esc(r.port)}</span>${src('host')}` : '<span class="text-muted">not set</span>'],
        ['Security', `${esc(SEC[r.security] ?? r.security)}${src('security')}`],
        ['Signs in as', r.username ? `<span class="mono">${esc(r.username)}</span>${src('username')}` : '<span class="text-muted">no sign-in</span>'],
        ['Its password', `${setBadge(r.password_set)}${r.password_unreadable ? ` ${badge('failed', 'unreadable')}` : ''}${src('password')}`],
        ['Mail is from', r.from ? `${esc(r.from)}${src('from_email')}` : '<span class="text-muted">not set</span>'],
        ['Sending', r.enabled ? badge('active', 'on') : badge('disabled', 'off')],
        ['Set in', r.console_row ? `this console${r.updated_at ? `, ${esc(r.updated_at)}${r.updated_by ? ` by ${esc(r.updated_by)}` : ''}` : ''}` : 'config.php (<code>smtp</code>, <code>emails.system</code>) — nothing is saved here yet'],
      ], warn, '<button type="button" class="btn btn-sm btn-secondary" data-edit="relay">Edit…</button>');
  }

  #editRelay() {
    const r = this.relay ?? {};
    const opt = (v, label) => `<option value="${v}"${(r.security ?? 'tls') === v ? ' selected' : ''}>${label}</option>`;
    inspectorForm({
      title: 'Mail relay',
      submitLabel: 'Save',
      body: `
        <p class="field-hint">Saved here, in ARC's database, for every mail it sends; once saved, this is the relay (config.php's smtp block counts only while nothing is saved here). A blank server removes these settings and config.php is in charge again.</p>
        <div class="form-grid-2">
          <label class="form-field"><span>Server *</span><input name="host" required maxlength="255" value="${esc(r.host ?? '')}" placeholder="smtp.example.com" autocomplete="off"></label>
          <label class="form-field"><span>Port *</span><input name="port" type="number" min="1" max="65535" value="${esc(r.port || 587)}"></label>
        </div>
        <label class="form-field"><span>Security</span>
          <select name="security">${opt('tls', 'STARTTLS (usually port 587)')}${opt('ssl', 'SMTPS, TLS from the start (port 465)')}${opt('', 'None, plain (port 25)')}</select></label>
        <div class="form-grid-2">
          <label class="form-field"><span>Sign in as</span><input name="username" maxlength="255" value="${esc(r.username ?? '')}" placeholder="leave blank if the relay needs no sign-in" autocomplete="off"></label>
          <label class="form-field"><span>Password</span><input name="password" type="password" maxlength="255" placeholder="${r.password_set ? 'unchanged unless typed' : 'none yet'}" autocomplete="new-password"></label>
        </div>
        ${r.password_set ? '<label class="form-check"><input type="checkbox" name="clear_password"> Remove the saved password</label>' : ''}
        <div class="form-grid-2">
          <label class="form-field"><span>Mail is from (address)</span><input name="from_email" type="email" maxlength="255" value="${esc(r.from_email ?? '')}" placeholder="noreply@example.com"></label>
          <label class="form-field"><span>From (name)</span><input name="from_name" maxlength="120" value="${esc(r.from_name ?? '')}" placeholder="Meridian"></label>
        </div>
        <label class="form-check"><input type="checkbox" name="enabled"${r.enabled !== false ? ' checked' : ''}> Sending is on</label>
        <p class="field-hint">Then press Test on the card: it connects, signs in, and sends one message to your own address.</p>`,
      onSubmit: async (f) => {
        const body = {
          host: f.host.value.trim(), port: +f.port.value, security: f.security.value,
          username: f.username.value.trim(), password: f.password.value,
          clear_password: !!f.clear_password?.checked,
          from_email: f.from_email.value.trim(), from_name: f.from_name.value.trim(),
          enabled: f.enabled.checked,
        };
        this.relay = await api.mail.update(body);
        toast(body.host ? 'Saved. Mail goes through it from now on; press Test to be sure.' : 'Removed: config.php\'s relay is in charge again.', 'success');
        this.#load();
      },
    });
  }

  #passwords(p) {
    return card('Passwords', null,
      'The rule every password set here meets — in this console, in account settings, directory and local accounts alike.', [
        ['At least', `${esc(p.min_length)} characters (${esc(p.recommended_length)} or more recommended)`],
        ['Must reach', esc(p.required)],
        ['Also refused', 'the most common passwords, and anything containing the account\'s name or username'],
        ['Set in', '<span class="mono">core/data/password-policy.json</span>'],
      ]);
  }
}

customElements.define('arc-settings', ArcSettings);
